import Img from '@core/components/Img';
import Route from '@core/components/Route';
import { RoutePath } from '@core/types/route';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Grid2 from '@shared/components/Grid2';
import { H2, H4 } from '@shared/components/Heading';
import List from '@shared/components/List';
import ListItem from '@shared/components/ListItem';
import Paragraph from '@shared/components/Paragraph';
import Section from '@shared/components/Section';
import Video from '@shared/components/Video';
import { FC } from 'react';

import { SectionDark1, SectionDark3 } from '../components/ColoredSection';
import GenericLayout from '../components/GenericLayout';
import PageImage from '../components/PageImage';
import RouteButton from '../components/RouteButton';
import { PageImageId } from '../types/page-image';

const SX_IMG: SxProps<Theme> = {
  maxHeight: { md: '400px', xs: 'none' },
  maxWidth: { md: '100%' },
  width: { md: 'auto', xs: '100%' },
};

const Home: FC = () => (
  <GenericLayout title='Kampfkunst, Bewegungslehre, Philosophie'>
    <PageImage id={PageImageId.Home} />

    <Section>
      <H2 center>
        Entdecke die Kraft von Systema – Körperliche Stärke, mentale Klarheit und innere Balance.
      </H2>
      <Paragraph center margin={{ bottom: '1.5rem' }}>
        Die altrussische Kampfkunst Systema "Solovev Defense" nach der Lehre von Meister Falko
        Zander bietet eine einzigartige Methode, die körperliche Übungen mit geistiger Entwicklung
        vereint. Ohne den Druck des Wettkampfs lernst Du, harmonische Bewegungen zu meistern und
        Dich kontinuierlich zu verbessern. Du lernst, mit immer weniger Aufwand mehr zu erreichen –
        körperlich und emotional ausgeglichen, wirst Du die größten Herausforderungen des Lebens mit
        innerer Ruhe und Selbstbewusstsein meistern.
      </Paragraph>
      <Video src={'302725898'} />
    </Section>

    <SectionDark3>
      <Grid2 spacing={{ md: '2rem', xs: '0' }}>
        <Grid2.Item>
          <H2>Drei Säulen: Kampfkunst, Psychologie und Weltanschauung</H2>
          <Paragraph>
            Systema basiert auf einem tiefen Verständnis des Menschen als Einheit von Körper, Geist
            und Seele.
          </Paragraph>
          <Paragraph>
            <Route href={RoutePath.FalkoZander}>Falko Zander</Route> hat die Prinzipien von Systema
            aufgegriffen und die Lehren in eine klar strukturierte und umfassende{' '}
            <Route href={RoutePath.KampfkunstRanking}>Methodik</Route> integriert und erweitert.
            Durch die Einführung der drei Säulen – Kampfkunst, Psychologie und Weltanschauung – hat
            er die Lehre von Systema verständlicher und zugänglicher gemacht. Diese spezifische
            Struktur der drei Säulen ist ein wesentlicher Bestandteil seiner Methodik.
          </Paragraph>
        </Grid2.Item>
        <Grid2.Item>
          <List bullets>
            <ListItem>
              <Route href={RoutePath.Kampfkunst}>Kampfkunst:</Route> Systema ist mehr als nur
              körperliches Training. Es ermöglicht, Prinzipien wie Entspannung, Präzision und
              Sensitivität praktisch zu erleben und erfahren. Diese Fähigkeiten steigern nicht nur
              Deine körperliche und geistige Leistungsfähigkeit, sondern bringen auch innere
              Sicherheit und Gelassenheit in allen Situationen.
            </ListItem>
            <ListItem>
              Psychologie: Die Vektor-Psychologie bietet einen tiefen Einblick in das menschliche
              Verhalten. Sie fördert das Verständnis für uns selbst und andere und kann dabei
              unterstützen, das soziale Zusammenleben zu verbessern. Emotionale Stabilität und das
              Verstehen der tieferen Hintergründe unseres Verhaltens sind der Schlüssel zu einem
              harmonischeren Miteinander und helfen, Herausforderungen mit Ruhe und Ausgeglichenheit
              zu begegnen.
            </ListItem>
            <ListItem>
              Weltanschauung: Die Kriegerlehre, die in Systema verankert ist, vermittelt wertvolle
              Glaubenssätze und Lebensphilosophien. Diese helfen Dir, ein starkes, gesundes und
              erfülltes Leben zu führen, indem sie Dein Selbstvertrauen und Deine
              Widerstandsfähigkeit stärken.
            </ListItem>
          </List>
        </Grid2.Item>
      </Grid2>
    </SectionDark3>

    <Section>
      <H2>Harmonie durch Einheit</H2>
      <Paragraph>
        Die drei Säulen von Systema sind eng miteinander verbunden und bedingen sich gegenseitig:
      </Paragraph>
      <Paragraph>
        Die körperliche Praxis der Kampfkunst stärkt nicht nur den Körper, sondern wirkt tief auf
        Deine mentale Stärke und innere Haltung. Durch das Training entwickelst Du Gelassenheit, die
        Dich in allen Lebenslagen begleitet. Gleichzeitig beeinflusst Dein geistiger Zustand, wie Du
        körperliche Techniken ausübst – je klarer und ruhiger Dein Geist, desto präziser und
        effektiver Dein Handeln.
      </Paragraph>
      <Paragraph>
        Durch diese Einheit von Kampfkunst, Psychologie und Weltanschauung wird Systema zu einer
        ganzheitlichen Lehre, die Dir nicht nur im Training, sondern in allen Lebensbereichen einen
        Weg zu mehr Harmonie und Stärke eröffnet.
      </Paragraph>
      <H4 bold margin={{ top: '2rem', y: '0' }} uppercase={false}>
        Sehnst Du Dich nach mehr Sicherheit, Gelassenheit und innerer Stärke?
      </H4>
      <Paragraph>
        Systema bietet Dir die Werkzeuge, um diese Ziele zu erreichen – und gleichzeitig einen
        klaren, ganzheitlichen Weg für Körper, Geist und Seele.
      </Paragraph>
    </Section>

    <SectionDark1>
      <Grid2 spacing='2rem'>
        <Grid2.Centered>
          <Route href={RoutePath.FalkoZander} sx={{ '&:after': { display: 'none' } }}>
            <Img
              alt='Falko Zander - Meine Vision'
              src='/img/illu-falko-laecheln-400.jpg'
              sx={SX_IMG}
            />
          </Route>
        </Grid2.Centered>
        <Grid2.Item>
          <Typography component='blockquote'>
            <H2>"Ich glaube,</H2>
            ...dass schon eine einzelne brennende Kerze die dunkelste Nacht erleuchten kann. Ich
            träume von einem ganzen Meer solcher Lichter - ein Meer von Menschen, die ihre innere
            Stärke gefunden haben und bereit sind, die Welt zu einem besseren Ort zu machen.
            Menschen, die das Schöne in sich und in der Menschheit erkennen und nicht nur ihr Licht
            sondern auch ihre Fähigkeiten, Einzigartigkeit und Liebe mit anderen teilen. Ich bin
            überzeugt, dass diejenigen, die an eine bessere Welt glauben, sie durch ihr inneres
            Feuer auch erschaffen werden."
          </Typography>
          <Paragraph bold margin={{ top: '1em', y: '0' }}>
            Falko Zander,
          </Paragraph>
          <Paragraph margin={{ bottom: '1em', y: '0' }} variant='body2'>
            Gründer und Direktor
            <br />
            Systema "Solovev Defense"
          </Paragraph>
          <Paragraph margin='0'>
            <RouteButton href={RoutePath.FalkoZander}>Mehr über Falko</RouteButton>
          </Paragraph>
        </Grid2.Item>
      </Grid2>
    </SectionDark1>
  </GenericLayout>
);

export default Home;
